import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, MapPin, Calendar, Clock } from "lucide-react";
import { useArticles } from "../api/hooks/useArticles";
import { assetUrl } from "../api/client";
import {
  getTranslatedTitle,
  getFirstCountryName,
} from "../api/utils/translations";

const SearchNavbar = () => {
  const { t, i18n } = useTranslation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const inputRef = useRef(null);

  const uiLang = (i18n.language || "en").split("-")[0];

  // Debounce search term
  useEffect(() => {
    if (searchTerm.length < 2) {
      setDebouncedSearchTerm("");
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // API-based search with proper nested field filtering
  const { data: searchResults, isLoading } = useArticles(
    {
      page: 1,
      limit: 6,
      search: debouncedSearchTerm || undefined,
    },
    {
      enabled: debouncedSearchTerm.length >= 2,
      staleTime: 30_000,
    }
  );

  const posts = useMemo(() => {
    return Array.isArray(searchResults?.data) ? searchResults.data : [];
  }, [searchResults?.data]);

  // Close search on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsSearchOpen(false);
        setSearchTerm("");
      }
    };

    if (isSearchOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isSearchOpen]);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isSearchOpen]);

  const handleSearchClick = () => {
    setIsSearchOpen(true);
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  const handleResultClick = () => {
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  return (
    <>
      {/* Search Button */}
      <button
        onClick={handleSearchClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
      </button>

      {/* Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-md"
            onClick={handleCloseSearch}
          />

          {/* Search Container */}
          <div className="relative h-full flex flex-col bg-white/95 backdrop-blur-md">
            {/* Search Header */}
            <div className="bg-white/95 backdrop-blur-md border-b border-gray-200">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder={t("search.placeholder", {
                        defaultValue: "Search stories...",
                      })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 text-lg bg-transparent border-none outline-none placeholder-gray-500"
                    />
                  </div>
                  <button
                    onClick={handleCloseSearch}
                    className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                    aria-label="Close search"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 bg-white/95 backdrop-blur-md">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {debouncedSearchTerm.length < 2 ? (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">
                      {t("search.startTyping", {
                        defaultValue: "Start typing to search stories...",
                      })}
                    </p>
                  </div>
                ) : isLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="text-gray-500 mt-4">
                      {t("search.searching", { defaultValue: "Searching..." })}
                    </p>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">
                      {t("search.noResults", {
                        defaultValue: "No stories found",
                      })}
                    </p>
                    <p className="text-gray-400 mt-2">
                      {t("search.tryDifferent", {
                        defaultValue: "Try different keywords",
                      })}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("search.results", { defaultValue: "Search Results" })}{" "}
                      ({posts.length})
                    </h3>
                    <div className="grid gap-4">
                      {posts.map((post) => {
                        const imgId = post.cover_image?.id || post.cover_image;
                        const imgSrc = imgId
                          ? assetUrl(imgId, {
                              width: 120,
                              height: 80,
                              fit: "cover",
                            })
                          : undefined;
                        const country = getFirstCountryName(
                          post.countries,
                          "—",
                          uiLang
                        );
                        const date = post.published_at
                          ? new Date(post.published_at).toLocaleDateString()
                          : "";
                        const title = getTranslatedTitle(
                          post.translations,
                          uiLang,
                          t("common.untitled", { defaultValue: "Untitled" })
                        );

                        return (
                          <Link
                            key={post.id}
                            to={`/blog/${post.id}`}
                            onClick={handleResultClick}
                            className="group flex items-center gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                          >
                            {/* Thumbnail */}
                            <div className="flex-shrink-0 w-20 h-16 rounded-md overflow-hidden bg-gray-200">
                              {imgSrc ? (
                                <img
                                  src={imgSrc}
                                  alt={title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                  {t("common.noImage", {
                                    defaultValue: "No image",
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
                                {title}
                              </h4>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {country}
                                </span>
                                <span className="flex items-center">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  {date}
                                </span>
                                {post.read_time && (
                                  <span className="flex items-center">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {post.read_time}
                                  </span>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchNavbar;
